/**
 * The bell, its unread badge, and the panel behind it.
 *
 * This is the delivery of record made visible. Web Push is a nudge that may be
 * refused, revoked, unsupported or simply unconfigured; this list is none of
 * those things, and every notification the system ever writes appears here
 * whether or not a single device is enrolled.
 *
 * It lives in the navy header rather than as a rail entry. A rail entry is a
 * place you go; the bell is a thing that changes while you are somewhere else,
 * and it has to be visible from every screen without taking one of the eleven
 * rail slots. The navy chrome is also the only surface where gold is permitted
 * (§0.1), which is what lets the unread count use the brand's own accent
 * instead of the red every other product reaches for.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api, type NotificationRow, type NotificationSeverity } from '../lib/api';
import { currentState, enablePush, disablePush, type PushState } from '../lib/push';

/** Poll interval. Long enough to be free, short enough to feel live. */
const POLL_MS = 60_000;

/** Panel width at rest, and the gutter it must never cross on a narrow screen. */
const PANEL_MAX_WIDTH = 352;
const VIEWPORT_GUTTER = 16;

/**
 * Where the panel sits, in viewport coordinates.
 *
 * The obvious implementation — `absolute right-0` under the bell — is wrong on
 * a phone, and wrong in a way that is invisible on a desktop. The header wraps
 * at 375px, which moves the bell inward from the right edge; a panel pinned to
 * the bell's right edge then extends past the left edge of the screen and
 * every row is clipped mid-word. Measuring the trigger and clamping against
 * the viewport is the fix, and it holds wherever the header decides to put the
 * bell at any width.
 */
function panelPosition(rect: DOMRect): { top: number; right: number; width: number } {
  const viewport = window.innerWidth;
  const width = Math.min(PANEL_MAX_WIDTH, viewport - VIEWPORT_GUTTER * 2);
  const fromRight = viewport - rect.right;
  // Never let the left edge cross the gutter, and never hang off the right.
  const maxRight = viewport - VIEWPORT_GUTTER - width;
  const right = Math.max(VIEWPORT_GUTTER, Math.min(fromRight, maxRight));
  return { top: rect.bottom + 8, right, width };
}

/**
 * Relative time, because an absolute timestamp forces the reader to do
 * subtraction to answer the only question they are actually asking: is this
 * still current?
 */
export function relativeTime(iso: string, now = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function BellIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
      <path d="M10.3 20a2 2 0 0 0 3.4 0" />
    </svg>
  );
}

/**
 * Severity is carried by a dot AND by the label text beneath it, never by
 * colour alone (§0.3). Someone reading this on a phone in direct sunlight, or
 * with any red/green deficiency, gets the same information.
 */
const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  info: 'Notice',
  warning: 'Attention',
  critical: 'Urgent',
};

function SeverityDot({ severity }: { severity: NotificationSeverity }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`notif-dot notif-dot--${severity} mt-1.5 flex-none`}
    />
  );
}

/**
 * What the push toggle says, in plain language, for every state it can be in.
 *
 * "Enable device alerts" is deliberately not a bare "Enable notifications":
 * the in-app list is already on and always will be, so a label implying
 * notifications are currently off would be false.
 */
function PushControl({
  state,
  busy,
  onEnable,
  onDisable,
}: {
  state: PushState;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}): JSX.Element {
  if (state === 'unsupported') {
    return (
      <p className="text-micro text-text-secondary">
        This browser cannot show device alerts. Notifications still arrive here.
      </p>
    );
  }
  if (state === 'requires_install') {
    return (
      <p className="text-micro text-text-secondary">
        On iPhone, add CALM Ops to your home screen to receive device alerts. Notifications still
        arrive here.
      </p>
    );
  }
  if (state === 'unconfigured') {
    return (
      <p className="text-micro text-text-secondary">
        Device alerts are not set up on this deployment. Notifications still arrive here.
      </p>
    );
  }
  if (state === 'denied') {
    return (
      <p className="text-micro text-text-secondary">
        Device alerts are blocked in your browser settings for this site. Notifications still arrive
        here.
      </p>
    );
  }
  if (state === 'enabled') {
    return (
      <button
        type="button"
        onClick={onDisable}
        disabled={busy}
        className="btn-secondary h-9 rounded-card px-3 text-micro font-medium uppercase disabled:opacity-60"
      >
        {busy ? 'Working…' : 'Turn off device alerts'}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onEnable}
      disabled={busy}
      className="btn-primary h-9 rounded-card px-3 text-micro font-medium uppercase disabled:opacity-60"
    >
      {busy ? 'Working…' : 'Enable device alerts'}
    </button>
  );
}

export function NotificationCentre(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [push, setPush] = useState<PushState>('disabled');
  const [pushBusy, setPushBusy] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number; width: number } | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /** The badge only. Cheap enough to run on a timer from every screen. */
  const refreshCount = useCallback(async () => {
    try {
      const { unread: n } = await api.unreadCount();
      setUnread(n);
    } catch {
      // A failed poll is not worth an error banner: the next one is 60s away
      // and the person has lost nothing in the meantime.
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.notifications({ limit: 30 });
      setRows(res.notifications);
      setUnread(res.unread);
    } catch {
      setError('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshCount();
    const timer = window.setInterval(() => void refreshCount(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    void loadList();
    void currentState().then(setPush);
  }, [open, loadList]);

  // Measured before paint, so the panel never appears at the wrong place and
  // jumps. Re-measured on scroll and resize because the header is not sticky:
  // scrolling moves the bell, and a panel left behind at its old coordinates
  // would float in the middle of the page detached from anything.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = (): void => {
      const rect = trigger.current?.getBoundingClientRect();
      if (rect) setPosition(panelPosition(rect));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // Escape closes and returns focus to the bell, so a keyboard user is not
  // dropped at the top of the document.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onClick = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !trigger.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  async function onEnable(): Promise<void> {
    setPushBusy(true);
    try {
      setPush(await enablePush());
    } catch {
      // A rejected prompt or a push service that would not issue a
      // subscription both land here. Re-reading the state is more honest than
      // guessing which it was.
      setPush(await currentState().catch(() => 'disabled' as PushState));
    } finally {
      setPushBusy(false);
    }
  }

  async function onDisable(): Promise<void> {
    setPushBusy(true);
    try {
      setPush(await disablePush());
    } finally {
      setPushBusy(false);
    }
  }

  async function openItem(row: NotificationRow): Promise<void> {
    if (!row.readAt) {
      // Optimistic: the row is marked read locally before the request settles,
      // because a badge that lags a tap by 300ms reads as a broken tap.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, readAt: new Date().toISOString() } : r)));
      setUnread((n) => Math.max(0, n - 1));
      await api.markNotificationsRead([row.id]).catch(() => void refreshCount());
    }
    if (row.href) {
      window.location.hash = row.href.replace(/^#/, '');
      setOpen(false);
    }
  }

  async function markAll(): Promise<void> {
    setRows((prev) => prev.map((r) => (r.readAt ? r : { ...r, readAt: new Date().toISOString() })));
    setUnread(0);
    await api.markAllNotificationsRead().catch(() => void loadList());
  }

  return (
    <div className="relative">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative flex h-9 w-9 items-center justify-center rounded-card border border-white/40 text-white hover:bg-white/10"
      >
        <BellIcon />
        {unread > 0 ? (
          // Gold on navy chrome: 7.88:1, and the one place §0.1 allows gold.
          <span className="notif-badge" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          ref={panel}
          role="dialog"
          aria-label="Notifications"
          // Fixed rather than absolute: the position is measured against the
          // viewport (see `panelPosition`) so a wrapped header on a phone
          // cannot push the panel off-screen. Height is capped so a long list
          // scrolls inside the panel instead of running past the fold.
          className="card fixed z-40 flex max-h-[min(70vh,32rem)] flex-col overflow-hidden p-0 text-left"
          style={{
            top: position?.top ?? 0,
            right: position?.right ?? VIEWPORT_GUTTER,
            width: position?.width ?? PANEL_MAX_WIDTH,
            // Hidden for the single frame between mount and measurement.
            visibility: position ? 'visible' : 'hidden',
          }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <p className="text-h3 font-semibold">Notifications</p>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => void markAll()}
                className="text-micro font-medium uppercase text-royal hover:underline"
              >
                Mark all read
              </button>
            ) : null}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && rows.length === 0 ? (
              <p className="px-4 py-6 text-body text-text-secondary">Loading…</p>
            ) : null}

            {error ? (
              <p role="alert" className="px-4 py-6 text-body text-warning">
                {error}
              </p>
            ) : null}

            {!loading && !error && rows.length === 0 ? (
              <div className="empty-state px-4 py-8 text-center">
                <p className="text-body">Nothing to report.</p>
                <p className="mt-1 text-micro text-text-secondary">
                  Reminders, sync failures and weather alerts arrive here.
                </p>
              </div>
            ) : null}

            <ul>
              {rows.map((row) => (
                <li key={row.id} className="border-b border-border last:border-b-0">
                  <button
                    type="button"
                    onClick={() => void openItem(row)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface ${
                      row.readAt ? '' : 'notif-row--unread'
                    }`}
                  >
                    <SeverityDot severity={row.severity} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        {/* Two lines, then ellipsis. A single truncated line
                            loses the part of a title that distinguishes it —
                            "Severe thunderstorm watch — Ne…" hides which town
                            is under the watch, which is the only thing the
                            reader needs from it. */}
                        <span
                          className={`notif-title text-body ${row.readAt ? '' : 'font-semibold'}`}
                        >
                          {row.title}
                        </span>
                        <span className="flex-none text-micro text-text-secondary">
                          {relativeTime(row.createdAt)}
                        </span>
                      </span>
                      {row.body ? (
                        <span className="mt-0.5 block text-micro text-text-secondary">
                          {row.body}
                        </span>
                      ) : null}
                      <span className="mt-1 block text-micro uppercase text-text-secondary">
                        {SEVERITY_LABEL[row.severity]}
                        {row.pillar ? ` · ${row.pillar}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-border px-4 py-3">
            <PushControl
              state={push}
              busy={pushBusy}
              onEnable={() => void onEnable()}
              onDisable={() => void onDisable()}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
